import { LOG_EVENT_TYPE, createLogEvent } from 'src/logger/log-event';

const DEFAULT_INVITE_VALIDATION_MESSAGE = 'Please check the email addresses and try again.';
const DEFAULT_INVITE_FAILURE_MESSAGE = 'Unable to create invitation(s). Please check the email addresses and try again.';
const ALREADY_EXISTS_MESSAGE = 'One or more of these users already have an account or a pending invitation.';
const RECIPIENT_INVITE_FAILURE_MESSAGE = 'This invitation could not be sent for this email address.';

const FAILED_COMPLETE_PROCESS_PREFIX = /^Failed complete process\s+[^:]+:\s*/i;

const TECHNICAL_INVITATION_ERROR_PATTERNS = [
    /\bselect\b[\s\S]*\bfrom\b/i,
    /invalid input syntax/i,
    /configuration_data/i,
    /\bwhere\b[\s\S]*\$\d+/i,
    /relation "[^"]+" does not exist/i,
];

/**
 * Detect backend/internal error text that should never be shown in the UI.
 *
 * @param {string} [message]
 * @returns {boolean}
 */
export const isTechnicalInvitationErrorMessage = (message = '') => {
    if (typeof message !== 'string' || !message.trim()) {
        return false;
    }

    if (message.length > 300) {
        return true;
    }

    return TECHNICAL_INVITATION_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * Replace leaked SQL/internal errors with a safe per-recipient message.
 *
 * @param {string} [errorMessage]
 * @returns {string}
 */
export const sanitizeInvitationRecipientErrorMessage = (errorMessage = '') => {
    if (typeof errorMessage !== 'string' || !errorMessage.trim()) {
        return RECIPIENT_INVITE_FAILURE_MESSAGE;
    }

    const withoutProcessPrefix = errorMessage.replace(FAILED_COMPLETE_PROCESS_PREFIX, '').trim();
    const candidate = withoutProcessPrefix || errorMessage.trim();

    if (isTechnicalInvitationErrorMessage(candidate)) {
        return RECIPIENT_INVITE_FAILURE_MESSAGE;
    }

    return candidate;
};

/**
 * @param {string} [message]
 * @returns {string}
 */
const sanitizeInvitationDisplayMessage = (message = '') => {
    if (typeof message !== 'string' || !message.trim()) {
        return '';
    }

    if (isTechnicalInvitationErrorMessage(message)) {
        return DEFAULT_INVITE_FAILURE_MESSAGE;
    }

    return message.trim();
};

/**
 * @param {Array<{ recipientEmail?: string, errorMessage?: string }>} errors
 * @returns {string}
 */
export const formatInvitationRecipientErrors = (errors = []) => {
    if (!Array.isArray(errors) || errors.length === 0) {
        return '';
    }

    return errors
        .map(({ recipientEmail, errorMessage }) => {
            const safeMessage = sanitizeInvitationRecipientErrorMessage(errorMessage);

            if (recipientEmail && safeMessage) {
                return `${recipientEmail}: ${safeMessage}`;
            }

            return safeMessage || recipientEmail || '';
        })
        .filter(Boolean)
        .join('. ');
};

/**
 * @param {object} [error]
 * @returns {string}
 */
export const getInvitationValidationInlineMessage = (error = {}) => {
    const recipientErrors = formatInvitationRecipientErrors(
        (error.errors || []).filter((entry) => entry?.recipientEmail),
    );
    if (recipientErrors) {
        return recipientErrors;
    }

    const fieldErrors = (error.errors || [])
        .filter((entry) => entry?.field && Array.isArray(entry.messages))
        .flatMap((entry) => entry.messages.map((message) => sanitizeInvitationDisplayMessage(message)))
        .filter(Boolean);
    if (fieldErrors.length > 0) {
        return fieldErrors.join('. ');
    }

    const messages = Array.isArray(error.message) ? error.message : [];
    const stringMessages = messages
        .map((message) => sanitizeInvitationDisplayMessage(message))
        .filter(Boolean);
    if (stringMessages.length > 0) {
        return stringMessages.join('. ');
    }

    const sanitizedMessage = sanitizeInvitationDisplayMessage(error.message);
    if (sanitizedMessage) {
        return sanitizedMessage;
    }

    return DEFAULT_INVITE_VALIDATION_MESSAGE;
};

/**
 * @param {object} [error]
 * @returns {{ title: string, content: string }}
 */
export const getInvitationHttpErrorToast = (error = {}) => {
    switch (error.statusCode) {
        case 401:
            return {
                title: 'Session expired',
                content: error.message || 'Please sign in again and try sending the invitation.',
            };
        case 403:
            return {
                title: 'Permission denied',
                content: 'You do not have permission to invite users.',
            };
        case 404:
            return {
                title: 'Invitation could not be sent',
                content: 'The selected role or agency could not be found. Refresh the page and try again.',
            };
        default: {
            const rawMessage = Array.isArray(error.message)
                ? error.message.join('. ')
                : error.message;
            const safeMessage = sanitizeInvitationDisplayMessage(rawMessage);

            return {
                title: 'Failed to send invitations',
                content: safeMessage || 'Something went wrong. Please try again.',
            };
        }
    }
};

const getRecipientErrorText = (error = {}) =>
    (error.errors || [])
        .map((entry) => entry?.errorMessage || '')
        .join(' ')
        .toLowerCase();

const isAlreadyExistsInvitationError = (error = {}) => {
    if (error.statusCode === 422) {
        return true;
    }

    const recipientErrorText = getRecipientErrorText(error);
    if (
        recipientErrorText.includes('already exists')
        || recipientErrorText.includes('already assigned to agency')
        || recipientErrorText.includes('pending invitation')
    ) {
        return true;
    }

    const combinedMessage = [
        ...(Array.isArray(error.message) ? error.message : []),
        typeof error.message === 'string' ? error.message : '',
    ]
        .join(' ')
        .toLowerCase();

    return combinedMessage.includes('unable to create invitation')
        || combinedMessage.includes('already have an account')
        || combinedMessage.includes('pending invitation');
};

/**
 * Normalize a 201 invitation response into UI/operation state.
 *
 * @param {object} response
 * @returns {object}
 */
export const processInvitationSendResponse = (response = {}) => {
    const successCount = response.successfulInvitationsProcessCount ?? 0;
    const failedCount = response.failedInvitationsProcessCount ?? 0;
    const inlineError = formatInvitationRecipientErrors(response.errors);

    if (failedCount === 0) {
        return {
            success: true,
            keepModalOpen: false,
            successCount,
            failedCount,
            partialSuccess: false,
            inlineError: '',
            data: response,
        };
    }

    if (successCount > 0) {
        return {
            success: false,
            keepModalOpen: true,
            successCount,
            failedCount,
            partialSuccess: true,
            inlineError,
            data: response,
        };
    }

    return {
        success: false,
        keepModalOpen: true,
        successCount: 0,
        failedCount,
        partialSuccess: false,
        inlineError: inlineError || DEFAULT_INVITE_FAILURE_MESSAGE,
        data: response,
    };
};

/**
 * Bounded request facts for invitation-send service and operation logs.
 * Never includes recipient emails or customized message content.
 *
 * @param {object} [payload]
 * @param {{ isLyt?: boolean }} [options]
 * @returns {object}
 */
export const getInvitationSendSafeRequestData = (payload = {}, { isLyt = false } = {}) => ({
    recipient_count: Array.isArray(payload.recipients) ? payload.recipients.length : 0,
    ...(payload.assigned_role ? { assigned_role: payload.assigned_role } : {}),
    ...(payload.agency_id ? { agency_id: payload.agency_id } : {}),
    jurisdiction_count: Array.isArray(payload.jurisdictions) ? payload.jurisdictions.length : 0,
    has_customized_message: Boolean(payload.customized_message),
    is_lyt_context: isLyt,
});

/**
 * Bounded batch-response facts for invitation-send service logs.
 *
 * @param {object} [raw]
 * @param {number} [statusCode]
 * @returns {object}
 */
export const selectInvitationBatchResponseData = (raw = {}, statusCode) => ({
    ...(typeof statusCode === 'number' ? { status_code: statusCode } : {}),
    successful_invitations_process_count: raw.successfulInvitationsProcessCount ?? 0,
    failed_invitations_process_count: raw.failedInvitationsProcessCount ?? 0,
    recipient_error_count: Array.isArray(raw.errors) ? raw.errors.length : 0,
});

/**
 * Classify a processed invitation-send result for operation logging.
 * Severity reflects business meaning, not HTTP status alone.
 *
 * @param {object} [result]
 * @param {object} [requestContext]
 * @returns {{ level: string, message: string, outcome: string, frontend_data: object }}
 */
export const getInvitationSendOperationLogMetadata = (result = {}, requestContext = {}) => {
    const frontendData = {
        recipient_count: requestContext.recipient_count ?? 0,
        ...(requestContext.assigned_role ? { assigned_role: requestContext.assigned_role } : {}),
        ...(requestContext.agency_id ? { agency_id: requestContext.agency_id } : {}),
        jurisdiction_count: requestContext.jurisdiction_count ?? 0,
        has_customized_message: Boolean(requestContext.has_customized_message),
        is_lyt_context: Boolean(requestContext.is_lyt_context),
        ...(typeof result.successCount === 'number' ? { success_count: result.successCount } : {}),
        ...(typeof result.failedCount === 'number' ? { failed_count: result.failedCount } : {}),
    };

    if (result.success && !result.partialSuccess) {
        return {
            level: 'info',
            message: 'User invitations sent',
            outcome: 'success',
            frontend_data: frontendData,
        };
    }

    if (result.partialSuccess) {
        return {
            level: 'warn',
            message: 'Some user invitations could not be sent',
            outcome: 'partial_success',
            frontend_data: {
                ...frontendData,
                reason: 'recipient_batch_partial_failure',
            },
        };
    }

    if (result.validationError) {
        return {
            level: 'info',
            message: result.alreadyExists
                ? 'User invitation rejected for existing recipient'
                : 'User invitation validation rejected',
            outcome: 'validation_rejected',
            frontend_data: {
                ...frontendData,
                validation_category: result.alreadyExists ? 'recipient_conflict' : 'invalid_input',
            },
        };
    }

    if (result.toast) {
        const statusCode = result.data?.statusCode;

        if (statusCode === 401) {
            return {
                level: 'warn',
                message: 'User invitation blocked by expired session',
                outcome: 'unauthorized',
                frontend_data: {
                    ...frontendData,
                    reason: 'session_expired',
                },
            };
        }

        if (statusCode === 403) {
            return {
                level: 'info',
                message: 'User invitation blocked by permissions',
                outcome: 'permission_denied',
                frontend_data: {
                    ...frontendData,
                    reason: 'insufficient_permissions',
                },
            };
        }

        if (statusCode === 404) {
            return {
                level: 'warn',
                message: 'User invitation resource not found',
                outcome: 'resource_not_found',
                frontend_data: {
                    ...frontendData,
                    reason: 'role_or_agency_not_found',
                },
            };
        }
    }

    if (result.keepModalOpen && !result.toast && !result.validationError) {
        return {
            level: 'info',
            message: 'User invitation batch rejected',
            outcome: 'batch_rejected',
            frontend_data: {
                ...frontendData,
                reason: 'all_recipients_failed',
            },
        };
    }

    return {
        level: 'error',
        message: 'User invitation request failed',
        outcome: 'failure',
        frontend_data: {
            ...frontendData,
            reason: result.data?.statusCode ? 'http_error' : 'service_request_failed',
            ...(typeof result.data?.statusCode === 'number'
                ? { http_status_code: result.data.statusCode }
                : {}),
        },
    };
};

/**
 * Build a correlated operation event for invitation send handling.
 *
 * @param {object} params
 * @param {string} params.codeLocation
 * @param {string} params.methodName
 * @param {string} params.transactionId
 * @param {object} params.result
 * @param {object} [params.requestContext]
 * @returns {object}
 */
export const createInvitationSendOperationEvent = ({
    codeLocation,
    methodName,
    transactionId,
    result,
    requestContext = {},
}) => {
    const metadata = getInvitationSendOperationLogMetadata(result, requestContext);

    return createLogEvent({
        eventType: LOG_EVENT_TYPE.OPERATION,
        codeLocation,
        methodName,
        level: metadata.level,
        message: metadata.message,
        outcome: metadata.outcome,
        transactionId,
        attributes: {
            frontend_data: metadata.frontend_data,
        },
    });
};

export const processInvitationSendError = (error = {}) => {
    if (isAlreadyExistsInvitationError(error)) {
        return {
            success: false,
            validationError: true,
            alreadyExists: true,
            keepModalOpen: true,
            inlineError: ALREADY_EXISTS_MESSAGE,
            data: error,
        };
    }

    if (error.statusCode === 400 || error.error === 'Bad Request') {
        return {
            success: false,
            validationError: true,
            keepModalOpen: true,
            inlineError: getInvitationValidationInlineMessage(error),
            data: error,
        };
    }

    const toast = getInvitationHttpErrorToast(error);

    return {
        success: false,
        keepModalOpen: true,
        toast,
        data: error,
    };
};
